const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    return new Promise((resolve) => {
      timeout = setTimeout(() => resolve(func(...args)), wait);
    });
  };
};

export const fetchUserData = async (
  user,
  getData,
  setUserData,
  setStudentData,
  setModuleData,
  setStudents,
  setLoading
) => {
  try {
    //console.log("fetchUserData called with user:", { email: user.email, type: user.type });
    const responseModule = await getData("proccess.php", "modules_V2");
    if (responseModule.success && Array.isArray(responseModule.data)) {
      setModuleData(responseModule.data);
    } else {
      console.error("Failed to fetch module data:", responseModule.message);
      setModuleData([]);
    }

    if (user.type === "student") {
      const response = await getData("proccess.php", "students_V2");
      if (response.success && Array.isArray(response.data)) {
        const matchedStudent = response.data.find(
          (s) => s.email === user.email
        );
        if (matchedStudent) {
          const courseId = matchedStudent.course != null ? String(matchedStudent.course) : null;
          const enrolledModule = courseId
            ? responseModule.data.find((m) => String(m.id) === courseId)
            : null;
          const enrolledModules = enrolledModule ? [enrolledModule] : [];
          setUserData(matchedStudent);
          setStudentData({ ...matchedStudent, enrolledModules });
        } else {
          //console.warn("Student data not found for email:", user.email);
          setUserData({ email: user.email, type: "student", fullName: "Student" });
          setStudentData(null);
        }
      } else {
        console.error("Failed to fetch student data:", response.message);
        setUserData({ email: user.email, type: "student", fullName: "Student" });
        setStudentData(null);
      }
    } else if (user.type === "parent") {
      const parentResponse = await getData("proccess.php", "parent_V2");
      if (parentResponse.success && Array.isArray(parentResponse.data)) {
        const matchedParent = parentResponse.data.find(
          (p) => p.email === user.email
        );
        if (matchedParent) {
          setUserData(matchedParent);
          const studentResponse = await getData("proccess.php", "students_V2");
          if (studentResponse.success && Array.isArray(studentResponse.data)) {
            const parentStudents = studentResponse.data
              .filter((s) => s.parent_id === matchedParent.id)
              .map((student) => {
                const courseId = student.course != null ? String(student.course) : null;
                const enrolledModule = courseId
                  ? responseModule.data.find((m) => String(m.id) === courseId)
                  : null;
                const enrolledModules = enrolledModule ? [enrolledModule] : [];
                return { ...student, enrolledModules };
              });
            setStudents(parentStudents);
            if (parentStudents.length > 0) {
              setStudentData(parentStudents[0]);
            } else {
              //console.warn("No students found for parent:", matchedParent.id);
              setStudentData(null);
            }
          } else {
            console.error("Failed to fetch student data:", studentResponse.message);
            setStudents([]);
            setStudentData(null);
          }
        } else {
          //console.warn("Parent data not found for email:", user.email);
          setUserData({ email: user.email, type: "parent", fullName: "Parent" });
          setStudents([]);
          setStudentData(null);
        }
      } else {
        console.error("Failed to fetch parent data:", parentResponse.message);
        setUserData({ email: user.email, type: "parent", fullName: "Parent" });
        setStudents([]);
        setStudentData(null);
      }
    }
  } catch (error) {
    console.error(`Error fetching user data: ${error.message}`);
    setUserData({ email: user.email || "unknown", type: user.type || "unknown", fullName: "User" });
    setStudentData(null);
    setModuleData([]);
    setStudents([]);
    throw error; // Let caller handle
  } finally {
    setLoading(false);
  }
};

export const fetchCourseMaterials = debounce(async (getData, module_id) => {
  try {
    const response = await getData("proccess.php", "lecture_note_V2");
    if (response.success && Array.isArray(response.data)) {
      return response.data.filter(note => note.module_ID === module_id) || [];
    } else {
      console.error("Failed to fetch module data:", response.message);
      return [];
    }
  } catch (error) {
    console.error(`Error fetching course materials: ${error.message}`);
    return [];
  }
}, 300);

export const fetchAssessments = debounce(async (getData, module_id) => {
  try {
    const response = await getData("proccess.php", "assessments_V2");
    if (response.success && Array.isArray(response.data)) {
      const currentDate = new Date();
      return response.data
        .filter(
          (assessment) =>
            assessment.module_ID === module_id &&
            new Date(assessment.end_date) > currentDate
        )
        .map((assessment) => ({
          ...assessment,
          questions: assessment.questions ? JSON.parse(assessment.questions) : [],
          answers: assessment.answers ? JSON.parse(assessment.answers) : [],
        }));
    } else {
      console.error("Failed to fetch assessments:", response.message);
      return [];
    }
  } catch (error) {
    console.error(`Error fetching assessments: ${error.message}`);
    return [];
  }
}, 300);

export const fetchStudentScores = debounce(async (getData, student_id, module_id) => {
  try {
    const response = await getData("proccess.php", "gradings_V2");
    if (response.success && Array.isArray(response.data)) {
      const allAssessments = await fetchAssessments(getData, module_id);
      const scores = response.data.filter((score) => score.student_id === student_id && allAssessments.some(a => a.id === score.assessment_id));
      scores.forEach((score) => {
        const assess = allAssessments.find(a => a.id === score.assessment_id);
        score.assessment_name = assess ? assess.name : "Unknown";
      });
      return scores;
    } else {
      console.error("Failed to fetch student scores:", response.message);
      return [];
    }
  } catch (error) {
    console.error(`Error fetching student scores: ${error.message}`);
    return [];
  }
}, 300);